import { useEffect } from 'react'
import { advanceEngagement, articleScrollDepth } from '../lib/articleEngagement'
import type { Publication } from '../types/blog'

const SESSION_KEY = 'bybeit-article-session'
const OPT_OUT_KEY = 'bybeit-analytics-disabled'
const IDLE_MS = 30 * 60 * 1000
let memoryOptOut = false
let memorySession: { id: string; at: number } | null = null

function getSession() {
  const now = Date.now()
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null')
    if (stored && typeof stored.id === 'string' && /^[0-9a-f-]{36}$/i.test(stored.id) && Number.isFinite(stored.at) && now >= stored.at && now - stored.at < IDLE_MS) memorySession = stored
  } catch { /* Storage may be unavailable. Use this page's in-memory session. */ }
  if (!memorySession || now - memorySession.at >= IDLE_MS) memorySession = { id: crypto.randomUUID(), at: now }
  memorySession.at = now
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(memorySession)) } catch { /* No persistent fallback. */ }
  return memorySession.id
}

export function articleTelemetryDisabled() {
  if (typeof navigator === 'undefined') return true
  if (memoryOptOut) return true
  if (navigator.doNotTrack === '1' || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return true
  try { return localStorage.getItem(OPT_OUT_KEY) === '1' } catch { return false }
}

export function setArticleTelemetryDisabled(disabled: boolean) {
  memoryOptOut = disabled
  try { localStorage.setItem(OPT_OUT_KEY, disabled ? '1' : '0') } catch { /* Browser may block storage. */ }
  window.dispatchEvent(new Event('bybeit-analytics-preference'))
}

export function useArticleTelemetry(slug: string, publication: Publication, ready: boolean) {
  useEffect(() => {
    if (!ready || import.meta.env.DEV || !['bybeit.app', 'neo-bybeit.web.app'].includes(window.location.hostname) || articleTelemetryDisabled()) return
    let stop = () => {}
    // Let React's development effect replay cancel before creating a page view.
    const start = window.setTimeout(() => {
      const eventId = crypto.randomUUID()
      const sessionId = getSession()
      const query = new URLSearchParams(window.location.search)
      const base = { slug, publication, eventId, sessionId }
      let acknowledged = false, stopped = false, depth = 0, lastSentSeconds = -1, lastSentDepth = -1
      let clock = { seconds: 0, lastTick: performance.now(), lastActivity: performance.now() }
      let visible = document.visibilityState === 'visible', focused = document.hasFocus()
      let attempts = 0, lastSessionTouch = performance.now()
      const post = (body: object) => fetch('/api/article-telemetry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true })
      const measureDepth = () => {
        const article = document.querySelector('.article-page .blog-article-body') ?? document.querySelector('.article-page')
        if (!article) return
        const rect = article.getBoundingClientRect()
        const seen = articleScrollDepth(rect.top, rect.height, window.innerHeight)
        depth = Math.max(depth, seen)
      }
      const tick = () => {
        clock = advanceEngagement(clock, performance.now(), visible, focused)
        if (visible) measureDepth()
      }
      const flush = () => {
        if (!acknowledged || articleTelemetryDisabled()) return
        if (!stopped) tick()
        const activeSeconds = Math.floor(clock.seconds)
        if (activeSeconds === lastSentSeconds && depth === lastSentDepth) return
        const sentDepth = depth
        void post({ ...base, type: 'engagement', activeSeconds, scrollDepth: sentDepth }).then(response => {
          if (response.ok) { lastSentSeconds = activeSeconds; lastSentDepth = sentDepth }
        }).catch(() => { /* The next heartbeat retries cumulative progress. */ })
      }
      let retryTimer = 0
      const sendView = () => {
        if (stopped || articleTelemetryDisabled()) return
        attempts++
        void post({ ...base, type: 'view', referrer: document.referrer ? (() => { try { return new URL(document.referrer).origin } catch { return '' } })() : '', utmSource: query.get('utm_source'), utmMedium: query.get('utm_medium'), language: navigator.language.split('-')[0] }).then(response => {
          if (!response.ok) throw new Error('Telemetry unavailable')
          acknowledged = true
          flush()
        }).catch(() => { if (!stopped && attempts < 3) retryTimer = window.setTimeout(sendView, attempts * 2000) })
      }
      const activity = () => {
        tick()
        clock.lastActivity = performance.now()
        if (clock.lastActivity - lastSessionTouch > 15000) { getSession(); lastSessionTouch = clock.lastActivity }
      }
      const visibility = () => { tick(); visible = document.visibilityState === 'visible'; focused = document.hasFocus(); clock.lastTick = performance.now(); flush() }
      const focus = () => { tick(); focused = document.hasFocus(); clock.lastTick = performance.now(); flush() }
      const preference = () => { if (articleTelemetryDisabled()) stop() }
      const timer = window.setInterval(tick, 1000)
      const heartbeat = window.setInterval(flush, 15000)
      window.addEventListener('scroll', activity, { passive: true })
      window.addEventListener('pointerdown', activity, { passive: true })
      window.addEventListener('keydown', activity)
      window.addEventListener('pagehide', flush)
      window.addEventListener('focus', focus)
      window.addEventListener('blur', focus)
      window.addEventListener('bybeit-analytics-preference', preference)
      window.addEventListener('storage', preference)
      document.addEventListener('visibilitychange', visibility)
      stop = () => {
        if (stopped) return
        tick()
        stopped = true
        flush()
        window.clearTimeout(retryTimer)
        window.clearInterval(timer)
        window.clearInterval(heartbeat)
        window.removeEventListener('scroll', activity)
        window.removeEventListener('pointerdown', activity)
        window.removeEventListener('keydown', activity)
        window.removeEventListener('pagehide', flush)
        window.removeEventListener('focus', focus)
        window.removeEventListener('blur', focus)
        window.removeEventListener('bybeit-analytics-preference', preference)
        window.removeEventListener('storage', preference)
        document.removeEventListener('visibilitychange', visibility)
      }
      if (visible) measureDepth()
      sendView()
    }, 0)
    return () => { window.clearTimeout(start); stop() }
  }, [slug, publication, ready])
}
